import { useMemo, useState } from "react";
import type { ColumnFiltersState, PaginationState } from "@tanstack/react-table";
import { Plus } from "lucide-react";

import { columnFiltersToQueryParams, pageCount } from "@/core/api/pagination";
import { DataTableClar } from "@/core/components/shared/data-table";
import { Button } from "@/core/components/ui/button";
import { useMerchantStores } from "@/features/merchant/api/use-merchant-stores";
import { MerchantStoreFormDialog } from "@/features/merchant/components/merchant-stores/merchant-store-form-dialog";
import {
  merchantStoresColumns,
  type MerchantStoreRow,
} from "@/features/merchant/components/merchant-stores/merchant-stores-columns";
import { useMerchantTranslation } from "@/features/merchant/i18n/use-merchant-translation";

/**
 * Section « Points de vente » du détail marchand : liste + dialog de
 * création. L'édition inline sera pilotée plus tard depuis la ligne (voir
 * roadmap phase 4 — la contrainte AGENTS.md interdit d'ajouter un menu de
 * ligne sans invalider les autres sections).
 */
export function MerchantStoresSection({ merchantId }: { merchantId: string }) {
  const { t: tm } = useMerchantTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const params = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      filters: columnFiltersToQueryParams(columnFilters),
    }),
    [pagination, columnFilters]
  );

  const { data, isLoading, isFetching, error, refetch } =
    useMerchantStores(merchantId, params);

  const rows = useMemo(
    () => (data?.items ?? []).filter((s): s is MerchantStoreRow => Boolean(s.id)),
    [data]
  );

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">
          {tm("merchant.stores.title")}
        </h2>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          {tm("merchant.stores.create")}
        </Button>
      </div>

      <DataTableClar
        data={rows}
        columnsClar={merchantStoresColumns}
        title={tm("merchant.stores.title")}
        exportFileName="merchant-stores"
        paginationMode="server"
        serverPageCount={data ? pageCount(data) : 0}
        totalItemsCount={data?.total ?? 0}
        pagination={pagination}
        onPaginationChange={setPagination}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        loading={{ isLoading, isFetching }}
        error={
          error
            ? { message: error.message, refetch: () => void refetch() }
            : undefined
        }
      />

      <MerchantStoreFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        merchantId={merchantId}
      />
    </section>
  );
}
